import Store from 'repatch';

import State from '../core/State';
import FacebookAccessToken from '../entity/FacebookAccessToken';
import FacebookAdAccount from '../entity/FacebookAdAccount';
import FacebookIntegratedAdAccount from '../entity/FacebookIntegratedAdAccount';
import ErrorPresenter from '../service/ErrorPresenter';
import FacebookOauthService, { AuthenticationCancelled } from '../service/FacebookOauthService';
import MessageDispatcher from '../service/MessageDispatcher';
import FacebookAdWebApi, { FacebookOperationUnauthorized } from '../service/persistent/FacebookAdWebApi';
import { FbAdAccountAlreadyIntegrated } from '../service/persistent/FacebookAdWebApi';
import { setUploadedAdPlatformKeys } from '../thunk/creativeSending';
import { disposeFacebookAccessToken, setFacebookAccessToken } from '../thunk/facebookAccessToken';
import {
  disposeFacebookAdAccounts,
  setFacebookAdAccounts,
  setIntegratedFacebookAdAccounts,
} from '../thunk/facebookAdAccounts';
import { finishIntegratedFacebookCommunication, startIntegratedFacebookAdAccounts } from '../thunk/facebookAdAccounts';
import {
  decrement as decrementFullScreenSpinnerNecessity,
  increment as incrementFullScreenSpinnerNecessity,
  setLoadingMessage,
} from '../thunk/fullScreenSpinner';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import { OrganizationDetailFragment } from './../generated/graphql';

class SendCreativeToFacebookUsecase {
  private store: Store<State>;
  private errorPresenter: ErrorPresenter;
  private facebookMarketingApiConnectionService: FacebookOauthService;
  private messageDispatcher: MessageDispatcher;
  private facebookAdWebApi: FacebookAdWebApi;

  constructor({
    store,
    errorPresenter,
    facebookMarketingApiConnectionService,
    messageDispatcher,
    facebookAdWebApi,
  }: {
    store: Store<State>;
    errorPresenter: ErrorPresenter;
    facebookMarketingApiConnectionService: FacebookOauthService;
    messageDispatcher: MessageDispatcher;
    facebookAdWebApi: FacebookAdWebApi;
  }) {
    this.store = store;
    this.errorPresenter = errorPresenter;
    this.facebookMarketingApiConnectionService = facebookMarketingApiConnectionService;
    this.messageDispatcher = messageDispatcher;
    this.facebookAdWebApi = facebookAdWebApi;
  }

  public fetchAdAccounts = async (): Promise<boolean | void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    let facebookAccessToken: FacebookAccessToken | undefined;

    try {
      facebookAccessToken = await this.fetchFacebookAccessToken();
    } catch (err) {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
      this.errorPresenter.showAsNonFatal(err);

      return;
    }
    if (!facebookAccessToken) {
      // canceled
      this.store.dispatch(decrementFullScreenSpinnerNecessity());

      return;
    }

    try {
      const facebookAdAccounts = await this.facebookAdWebApi.getAllAccessibleAdAccounts(facebookAccessToken);
      this.store.dispatch(setFacebookAdAccounts(facebookAdAccounts));

      return true;
    } catch (err) {
      if (err instanceof FacebookOperationUnauthorized) {
        this.errorPresenter.showAsNonFatal(new Error('message.permit_ads_management_on_facebook'));
        return;
      }

      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public sendCreative = async (
    deliverable: {
      id: string;
      creative: {
        id: string;
      };
    },
    facebookAdAccount: FacebookAdAccount,
  ): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    const facebookAccessToken = this.store.getState().facebookAccessToken;
    if (!facebookAccessToken) {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
      throw new Error('Facebook Access Token must not empty');
    }

    try {
      this.store.dispatch(setLoadingMessage('message.creative_now_sending'));

      await this.facebookAdWebApi.sendCreativeToFacebookAdAccount({
        creative: deliverable.creative,
        facebookAdAccount,
        facebookAccessToken,
      });

      this.messageDispatcher.notifyUnimportant('message.creative_has_sent_to_facebook_ad_asset_library', {
        action: 'message.creative_has_sent_to_facebook_ad_asset_library.action',
        onClickAction: () => this.transitionToFacebookAdAssetLibrary(facebookAdAccount),
      });

      const uploadedAdPlatformKeys =
        this.store.getState().creativeSending.uploadedAdPlatformKeys.get(deliverable.id) || [];
      this.store.dispatch(setUploadedAdPlatformKeys(deliverable.id, [...uploadedAdPlatformKeys, 'facebook']));
    } catch (err) {
      if (err instanceof FacebookOperationUnauthorized) {
        this.errorPresenter.showAsNonFatal(new Error('message.permit_ads_management_on_facebook'));
        return;
      }

      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public fetchIntegratedFacebookAdAccounts = async (orgId?: string): Promise<void> => {
    this.store.dispatch(startIntegratedFacebookAdAccounts());

    let organizationId;
    if (!orgId) {
      const state: State = this.store.getState();
      let organization: OrganizationDetailFragment | null = null;

      if (state.lastAccessedOrganization) {
        organization = state.lastAccessedOrganization;
      } else {
        organization = state.availableOrganizations.items[0];
      }
      this.store.dispatch(setLastAccessedOrganization(organization));
      organizationId = organization ? organization.id : '';
    } else {
      organizationId = orgId;
    }

    try {
      const integratedFacebookAdAccounts = await this.facebookAdWebApi.getAllIntegratedAdAccounts(organizationId);
      this.store.dispatch(setIntegratedFacebookAdAccounts(integratedFacebookAdAccounts));
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    }
  };

  public integrateFacebookAdAccount = async (facebookAdAccount: FacebookAdAccount): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    const facebookAccessToken = this.store.getState().facebookAccessToken;
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    this.store.dispatch(setLastAccessedOrganization(organization));

    if (!facebookAccessToken) {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());

      throw new Error('Facebook Access Token must not empty');
    }
    try {
      this.store.dispatch(setLoadingMessage('message.creative_now_integrate'));
      await this.facebookAdWebApi.integrateAdAccount({
        organizationId: organization ? organization.id : '',
        facebookAdAccountId: facebookAdAccount.id,
        facebookAccessToken,
      });
      this.messageDispatcher.notifyUnimportant('message.facebook_integrate');
    } catch (err) {
      if (err instanceof FacebookOperationUnauthorized) {
        this.errorPresenter.showAsNonFatal(new Error('message.permit_ads_management_on_facebook'));
        return;
      }
      if (err instanceof FbAdAccountAlreadyIntegrated) {
        this.errorPresenter.showAsNonFatal(new Error('message.facebook_integrate_already'));
        return;
      }
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
    this.fetchIntegratedFacebookAdAccounts();
  };

  public revokeFacebookAdAccount = async (facebookAdAccount: FacebookIntegratedAdAccount): Promise<void> => {
    this.store.dispatch(startIntegratedFacebookAdAccounts());

    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    this.store.dispatch(setLastAccessedOrganization(organization));

    try {
      await this.facebookAdWebApi.revokeAdAccount({
        organizationId: organization ? organization.id : '',
        facebookAdAccountId: facebookAdAccount.id,
      });
      this.store.dispatch(finishIntegratedFacebookCommunication());
      this.messageDispatcher.notifyUnimportant('message.facebook_revoke');
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
    }
    this.fetchIntegratedFacebookAdAccounts();
  };

  public disposeAdAccounts = (): void => {
    this.store.dispatch(disposeFacebookAdAccounts());
  };

  public disposeAccessToken = (): void => {
    this.store.dispatch(disposeFacebookAccessToken());
  };

  private fetchFacebookAccessToken = async (): Promise<string | undefined> => {
    let shortLivedToken: string;

    try {
      shortLivedToken = await this.facebookMarketingApiConnectionService.authenticate();
    } catch (err) {
      if (err instanceof AuthenticationCancelled) {
        return;
      }

      throw err;
    }

    this.store.dispatch(setFacebookAccessToken(shortLivedToken));

    return shortLivedToken;
  };

  private transitionToFacebookAdAssetLibrary = (facebookAdAccount: FacebookAdAccount) => {
    window.open(
      `//business.facebook.com/asset_library/ad_accounts?business_id=${facebookAdAccount.business.id}&global_scope_id=${facebookAdAccount.business.id}&object_id=${facebookAdAccount.accountId}`,
      '_blank',
    );
  };
}

export default SendCreativeToFacebookUsecase;
